import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "circle",
    "flashWhite",
    "flashGold",
    "homeSection",
    "formSection",
    "doneSection",
    "nameInput",
    "status",
    "hint",
    "createBtn",
    "finalUrl",
    "urlPreview",
    "slugPreview",
    "signalSlug",
    "suggestions"
  ]

  static values = {
    verb: String,
    domainId: Number,
    examples: Array,
    suffixes: Array
  }

  connect() {
    this.state = { final: "" }
    this.isConverting = false  // Flag pour bloquer cleanup pendant conversion
    this.validateSeq = 0       // Séquencement des checks de dispo (réponses obsolètes ignorées)
    this.suggestionsSeq = 0

    // Générer ou récupérer client_id pour fingerprint persistant
    this.clientId = this.getOrCreateClientId()

    // Setup cleanup listeners (sendBeacon)
    this.setupCleanupListeners()

    // Historique navigateur : back/forward naviguent entre les écrans du wizard
    this.historyDepth = 0
    this.boundPopstate = this.handlePopstate.bind(this)
    window.addEventListener('popstate', this.boundPopstate)

    // Deeplink : ?step=form → aller directement sur le formulaire
    const params = new URLSearchParams(window.location.search)
    const initialStep = params.get('step') === 'form' ? 'form' : 'home'
    // Merge avec l'état existant pour préserver la restauration Turbo
    history.replaceState({ ...history.state, wizardStep: initialStep }, '')

    if (params.get('step') === 'form') {
      this.homeSectionTarget.classList.remove('active')
      this.formSectionTarget.classList.add('active')

      const prefillSlug = params.get('slug')
      if (prefillSlug) {
        // Pre-remplir et valider automatiquement
        const normalized = this.normalize(prefillSlug)
        this.nameInputTarget.value = normalized
        if (this.hasSlugPreviewTarget) {
          this.slugPreviewTarget.textContent = normalized
        }
        // Lancer la validation apres un court delai (DOM ready)
        setTimeout(() => this.validate(), 150)
      } else {
        setTimeout(() => this.nameInputTarget.focus(), 100)
      }

      this.startPlaceholderRotation()
      this.track('landing_wizard_view', { step: 'form', deeplink: true, prefill: !!prefillSlug })
    } else {
      this.track('landing_wizard_view', { step: 'home' })
    }
  }

  disconnect() {
    clearTimeout(this.slugCheckTrackingTimeout)
    clearTimeout(this.suggestionsTimeout)
    clearInterval(this.placeholderInterval)
    window.removeEventListener('popstate', this.boundPopstate)
    this.removeCleanupListeners()
  }

  // Navigation : Home → Form
  goToForm() {
    this.track('landing_wizard_step', { step: 'form', action: 'start_creation' })
    this.pushStep('form')
    this.showStep('form')
  }

  // Navigation : Form → Home (retour)
  goToHome() {
    this.track('landing_wizard_step', { step: 'home', action: 'back_from_form' })

    if (this.historyDepth > 0) {
      // Une entrée wizard a été empilée : le back navigateur fait la transition (popstate)
      history.back()
    } else {
      // Arrivée directe sur ?step=form : transition sans toucher à la pile
      const url = new URL(window.location)
      url.searchParams.delete('step')
      history.replaceState({ ...history.state, wizardStep: 'home' }, '', url)
      this.showStep('home')
    }
  }

  // === HISTORIQUE NAVIGATEUR (back/forward entre écrans) ===

  pushStep(step) {
    const url = new URL(window.location)
    url.searchParams.set('step', 'form')
    history.pushState({ wizardStep: step }, '', url)
    this.historyDepth++
  }

  handlePopstate(event) {
    const step = event.state?.wizardStep
    // État sans wizardStep : entrée gérée par Turbo (restauration pleine page)
    if (!step) return

    this.historyDepth = Math.max(0, this.historyDepth - 1)
    this.showStep(step)
  }

  showStep(step) {
    // Écran done sans réservation en mémoire (reload, forward) → formulaire
    if (step === 'done' && !this.reservationUUID) step = 'form'

    this.triggerTransition(() => {
      this.homeSectionTarget.classList.toggle('active', step === 'home')
      this.formSectionTarget.classList.toggle('active', step === 'form')
      this.doneSectionTarget.classList.toggle('active', step === 'done')
      document.body.classList.toggle('in-space', step === 'done')

      if (step === 'home') {
        this.resetForm()
      } else if (step === 'form') {
        setTimeout(() => this.nameInputTarget.focus(), 100)
        this.startPlaceholderRotation()
      }
    })
  }

  // Validation du slug en temps réel
  async validate() {
    const name = this.normalize(this.nameInputTarget.value)
    this.nameInputTarget.value = name

    // Mise à jour de la preview en temps réel
    if (this.hasSlugPreviewTarget) {
      this.slugPreviewTarget.textContent = name || '...'
    }

    if (!name) {
      this.clearStatus()
      this.nameInputTarget.classList.remove('checking')
      return
    }

    // Minimum 3 caractères avant de vérifier la disponibilité
    if (name.length < 3) {
      this.statusTarget.textContent = "3 caractères minimum."
      this.statusTarget.className = "status"
      this.createBtnTarget.disabled = true
      this.state.final = ""
      this.nameInputTarget.classList.remove('checking')
      this.clearSuggestions()
      return
    }

    // Ajouter classe checking pour effet shimmer
    this.nameInputTarget.classList.add('checking')

    // Séquencement : seule la réponse du dernier input compte
    const seq = ++this.validateSeq

    try {
      // check_availability est accessible sur tous domaines (routing fix)
      // client_id : exclut sa propre réservation (reprendre son slug reste possible)
      const url = `/check_availability?slug=${name}&domain_id=${this.domainIdValue}&client_id=${this.clientId}`

      const response = await fetch(url)
      const data = await response.json()

      if (seq !== this.validateSeq) return

      this.nameInputTarget.classList.remove('checking')

      if (data.available) {
        this.showAvailable(name)
        this.debouncedTrackSlugCheck({ slug: name, available: true })
      } else {
        this.showUnavailable()
        this.debouncedTrackSlugCheck({ slug: name, available: false })
      }
    } catch (error) {
      console.error('Error checking availability:', error)
      this.nameInputTarget.classList.remove('checking')
      this.showUnavailable()
    }
  }

  // Création de l'espace (réservation landing)
  async createSpace() {
    if (!this.state.final) return

    try {
      const response = await fetch('/api/landing_reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          slug: this.state.final,
          domain_id: this.domainIdValue,
          client_id: this.clientId
        })
      })

      const data = await response.json()

      if (response.ok) {
        // Stocker UUID pour cleanup
        this.reservationUUID = data.uuid

        // Track reservation success
        this.track('landing_reservation_created', {
          slug: this.state.final,
          uuid: data.uuid
        })

        // Entrée historique : back depuis l'écran done ramène au formulaire
        this.pushStep('done')

        // Transition vers écran "done" avec Big Bang (flash + shake)
        this.triggerBigBang(() => {
          this.updateDoneScreen(data)
          this.formSectionTarget.classList.remove('active')
          this.doneSectionTarget.classList.add('active')
        })
      } else {
        this.track('landing_reservation_failed', {
          slug: this.state.final,
          error: data.error
        })
        alert(data.error || 'Une erreur est survenue')
      }
    } catch (error) {
      console.error('Error creating reservation:', error)
      alert('Impossible de créer la réservation')
    }
  }

  // Redirection vers création de compte avec UUID
  openEditor() {
    const uuid = this.reservationUUID
    if (!uuid) {
      alert('Erreur : UUID de réservation manquant')
      return
    }

    // BLOQUER CLEANUP IMMÉDIATEMENT (synchrone, avant tout)
    this.isConverting = true

    // Track conversion
    this.track('landing_conversion', {
      slug: this.state.final,
      uuid: uuid
    })

    // Construire URL
    const signupUrl = `${window.location.protocol}//${this.getMainDomain()}/create-account?reservation_uuid=${uuid}&verb=${this.verbValue}`

    // Warp speed animation puis redirect
    this.triggerWarpSpeed(() => {
      window.location.href = signupUrl
    })
  }

  // Helpers
  normalize(str) {
    return (str || "").toLowerCase().trim().replace(/[^a-z0-9-]/g, "")
  }

  triggerTransition(callback) {
    this.circleTarget.classList.add('go')

    // Fade out de la section courante
    const currentSection = document.querySelector('section.active')
    if (currentSection) {
      currentSection.style.opacity = '0'
    }

    setTimeout(() => {
      callback()
      this.circleTarget.classList.remove('go')

      // Reset opacity pour la nouvelle section
      if (currentSection) {
        currentSection.style.opacity = ''
      }
    }, 450)
  }

  triggerBigBang(callback) {
    // 1. Circle commence immédiatement (build-up)
    this.circleTarget.classList.add('go')
    document.body.classList.add('in-space')

    // Fade out de la section courante
    const currentSection = document.querySelector('section.active')
    if (currentSection) {
      currentSection.style.opacity = '0'
    }

    // 2. Flash blanc explosif au moment du climax (450ms)
    setTimeout(() => {
      this.flashWhiteTarget.classList.add('active')
      callback()

      // Retirer flash après animation
      setTimeout(() => {
        this.flashWhiteTarget.classList.remove('active')
      }, 400)
    }, 450)

    // 3. Cleanup circle
    setTimeout(() => {
      this.circleTarget.classList.remove('go')
      if (currentSection) {
        currentSection.style.opacity = ''
      }
    }, 900)
  }

  triggerWarpSpeed(callback) {
    // Flash doré dédié pour transition finale (plus doux)
    this.flashGoldTarget.style.opacity = '1'

    setTimeout(() => {
      callback()
    }, 300)
  }

  showAvailable(slug) {
    this.hintTarget.classList.remove('active')
    this.statusTarget.textContent = "Disponible."
    this.statusTarget.className = "status ok"
    this.state.final = slug
    this.createBtnTarget.disabled = false
    this.clearSuggestions()
  }

  showUnavailable() {
    this.statusTarget.textContent = ""
    this.statusTarget.className = "status"
    this.hintTarget.classList.add('active')
    this.state.final = ""
    this.createBtnTarget.disabled = true
    this.scheduleSuggestions()
  }

  clearStatus() {
    this.statusTarget.textContent = ""
    this.statusTarget.className = "status"
    this.hintTarget.classList.remove('active')
    this.createBtnTarget.disabled = true
    this.state.final = ""
    this.clearSuggestions()
  }

  resetForm() {
    this.nameInputTarget.value = ""
    this.clearStatus()
  }

  // === PLACEHOLDERS TOURNANTS (exemples par verbe) ===

  startPlaceholderRotation() {
    if (!this.hasExamplesValue || this.examplesValue.length === 0) return
    if (this.placeholderInterval) return

    let index = 0
    this.nameInputTarget.placeholder = this.examplesValue[0]

    this.placeholderInterval = setInterval(() => {
      // Ne pas distraire pendant la saisie
      if (this.nameInputTarget.value) return
      index = (index + 1) % this.examplesValue.length
      this.nameInputTarget.placeholder = this.examplesValue[index]
    }, 2400)
  }

  // === SUGGESTIONS DE VARIANTES (slug pris) ===

  scheduleSuggestions() {
    clearTimeout(this.suggestionsTimeout)
    const base = this.normalize(this.nameInputTarget.value)
    if (!base) return

    // Attendre que la saisie se stabilise avant de chercher des variantes
    this.suggestionsTimeout = setTimeout(() => this.fetchSuggestions(base), 600)
  }

  async fetchSuggestions(base) {
    if (!this.hasSuffixesValue || this.suffixesValue.length === 0) return

    const candidates = this.suffixesValue
      .map(suffix => `${base}-${suffix}`)
      .filter(slug => slug.length <= 30 && slug !== base)

    const seq = ++this.suggestionsSeq

    const results = await Promise.all(candidates.map(async (slug) => {
      try {
        const response = await fetch(`/check_availability?slug=${slug}&domain_id=${this.domainIdValue}&client_id=${this.clientId}`)
        const data = await response.json()
        return data.available ? slug : null
      } catch {
        return null
      }
    }))

    // Réponse obsolète ou slug modifié entre-temps : ne rien afficher
    if (seq !== this.suggestionsSeq) return
    if (this.normalize(this.nameInputTarget.value) !== base) return

    this.renderSuggestions(results.filter(Boolean))
  }

  renderSuggestions(slugs) {
    if (!this.hasSuggestionsTarget) return

    // Slugs normalisés [a-z0-9-] : sûrs pour innerHTML
    this.suggestionsTarget.innerHTML = slugs.map(slug => `
      <button type="button" class="suggestion-chip" data-action="click->landing#pickSuggestion" data-slug="${slug}">
        ${this.verbValue}.la/<span class="suggestion-slug">${slug}</span>
      </button>
    `).join('')
  }

  pickSuggestion(event) {
    const slug = event.currentTarget.dataset.slug
    this.track('landing_slug_suggestion_pick', { slug })
    this.nameInputTarget.value = slug
    this.clearSuggestions()
    this.validate()
  }

  clearSuggestions() {
    clearTimeout(this.suggestionsTimeout)
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.innerHTML = ''
    }
  }

  updateDoneScreen(data) {
    this.reservationUUID = data.uuid

    // Afficher l'URL complète en minuscules, taille adaptée à la longueur
    if (this.hasSignalSlugTarget) {
      this.signalSlugTarget.textContent = data.slug

      const urlElement = this.signalSlugTarget.closest('.signal-url')
      if (urlElement) {
        const totalLength = `${this.verbValue}.la/${data.slug}`.length
        urlElement.classList.toggle('long', totalLength > 26)
      }
    }
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  getMainDomain() {
    // Retourne le domaine principal (rholala.la ou localhost)
    const hostname = window.location.hostname
    const isDev = hostname.includes('localhost') ||
                  hostname.includes('127.0.0.1') ||
                  hostname.endsWith('.test') ||
                  hostname.endsWith('.local')
    return isDev ? 'localhost:3000' : 'rholala.la'
  }

  // Keyboard shortcuts
  handleKeydown(event) {
    if (event.key === 'Escape' && this.formSectionTarget.classList.contains('active')) {
      this.goToHome()
    }

    if (event.key === 'Enter') {
      if (this.homeSectionTarget.classList.contains('active')) {
        this.goToForm()
      } else if (this.formSectionTarget.classList.contains('active') && !this.createBtnTarget.disabled) {
        this.createSpace()
      }
    }
  }

  // === CLEANUP MANAGEMENT (sendBeacon) ===

  setupCleanupListeners() {
    // pagehide : event le plus fiable pour cleanup (fired quand page vraiment déchargée)
    this.boundPageHide = this.handlePageHide.bind(this)
    window.addEventListener('pagehide', this.boundPageHide)

    // beforeunload : fallback pour anciens navigateurs
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this)
    window.addEventListener('beforeunload', this.boundBeforeUnload)
  }

  removeCleanupListeners() {
    if (this.boundPageHide) {
      window.removeEventListener('pagehide', this.boundPageHide)
    }
    if (this.boundBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundBeforeUnload)
    }
  }

  handlePageHide() {
    // Cleanup uniquement si une réservation existe ET pas en cours de conversion
    if (this.reservationUUID && !this.isConverting) {
      this.sendCleanup()
    }
  }

  handleBeforeUnload() {
    // Cleanup uniquement si une réservation existe ET pas en cours de conversion
    if (this.reservationUUID && !this.isConverting) {
      this.sendCleanup()
    }
  }

  sendCleanup() {
    // sendBeacon garantit l'envoi même si page se ferme
    const url = '/api/landing_reservations/cleanup'
    const payload = JSON.stringify({ uuid: this.reservationUUID })

    // sendBeacon envoie le payload comme body brut
    navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }))
  }

  // === CLIENT ID (FINGERPRINT) ===

  getOrCreateClientId() {
    const storageKey = 'rholala_client_id'
    let clientId = localStorage.getItem(storageKey)

    if (!clientId) {
      // Générer UUID v4 simple
      clientId = this.generateUUID()
      localStorage.setItem(storageKey, clientId)
    }

    return clientId
  }

  generateUUID() {
    // Simple UUID v4 generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  // === TRACKING (AHOY) ===

  debouncedTrackSlugCheck(properties) {
    clearTimeout(this.slugCheckTrackingTimeout)
    this.slugCheckTrackingTimeout = setTimeout(() => {
      this.track('landing_slug_check', properties)
    }, 500)
  }

  track(eventName, properties = {}) {
    // Envoyer event tracking vers API Ahoy
    const url = '/api/landing_reservations/track'

    const data = {
      event: eventName,
      verb: this.verbValue,
      domain_id: this.domainIdValue,
      properties: properties
    }

    // Fire and forget (pas de await)
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify(data)
    }).catch(error => {
      console.debug('Tracking error (non-blocking):', error)
    })
  }
}
